import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageTitle } from "@/lib/title";
import { getCourseTheme } from "@/lib/courseTheme";
import type { Course } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, statusTone } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { CreateCourseModal } from "@/components/courses/CreateCourseModal";

export function CourseCatalogPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const canCreate = user?.role === "admin" || user?.role === "instructor";

  // Memoized so the action element's identity is stable across re-renders —
  // usePageTitle stores it in context, and an unstable identity here would
  // re-trigger its effect on every render.
  const newCourseAction = useMemo(
    () =>
      canCreate ? (
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> New course
        </Button>
      ) : undefined,
    [canCreate],
  );

  usePageTitle("Courses", {
    searchValue: search,
    onSearchChange: setSearch,
    searchPlaceholder: "Search courses...",
    action: newCourseAction,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["courses", { search }],
    queryFn: () =>
      api.getPaginated<Course>(`/api/v1.0/courses?page=1&page_size=50&search=${encodeURIComponent(search)}`),
  });

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Browse and manage learning content.</p>

      {isLoading ? (
        <FullPageSpinner />
      ) : !data?.data.length ? (
        <EmptyState icon={Search} title="No courses found" description="Try a different search or create a new course." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {data.data.map((course) => {
            const { Icon, gradient } = getCourseTheme(course.title);
            return (
              <Link key={course.id} to={`/courses/${course.id}`}>
                <Card className="h-full transition-shadow hover:shadow-md">
                  <div className={`flex h-32 items-center justify-center rounded-t-xl bg-gradient-to-br ${gradient} text-white`}>
                    <Icon className="h-10 w-10 opacity-80" />
                  </div>
                  <CardBody>
                    <div className="mb-2 flex items-center justify-between">
                      <Badge tone={statusTone(course.status)}>{course.status}</Badge>
                      <span className="text-xs text-slate-400">{course.duration_minutes} min</span>
                    </div>
                    <h3 className="font-semibold text-slate-900">{course.title}</h3>
                    {course.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{course.description}</p>
                    )}
                  </CardBody>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && <CreateCourseModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
